import { FontColorEnum, FormField, FormHint, FormInput, FormLabel } from '@acceso/design-system';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TInputNumberProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
};

export function InputNumber<T extends FieldValues>({
  form,
  name,
  required,
  disabled,
  label,
  placeholder,
  min,
  max,
}: TInputNumberProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';

  return (
    <FormField>
      {label && (
        <FormLabel htmlFor={name}>
          {label}
          {labelSuffix}
        </FormLabel>
      )}
      <Controller
        disabled={disabled}
        name={name}
        control={control}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
          min:
            min !== undefined
              ? { value: min, message: t('El valor mínimo es {{min}}', { min }) }
              : undefined,
          max:
            max !== undefined
              ? { value: max, message: t(`El valor máximo es {{max}}`, { max }) }
              : undefined,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <FormInput
              type="number"
              disabled={disabled}
              value={value}
              placeholder={placeholder || label}
              onChange={e => onChange(e)}
              min={min}
              max={max}
              hasError={!!error}
            />
            {error && (
              <FormHint color={FontColorEnum.Error20} type="error">
                {error.message}
              </FormHint>
            )}
          </>
        )}
      />
    </FormField>
  );
}
